import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { makeStyles } from '@mui/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { BaseProps } from 'shared/types/base';
import { CustomToolbarItem } from 'shared/types/customToolbar';
import { Theme, Tooltip } from '@mui/material';
import { ConfirmModal } from 'modules/common/Modals/ConfirmModal';
import { Box } from '@scope/scope-one-ui/core';

type CustomToolbarProps = {
  menuItems?: CustomToolbarItem[];
  onApplySearchFiltersClick?: () => void;
  onApplyFiltersClick?: () => void;
  onCustomButtonClick?: () => void;
  isFiltered?: boolean;
  showFilters: boolean;
  showSearchFilters?: boolean;
  customButtonLabel?: string;
  popoverFilters?: JSX.Element;
} & BaseProps;

const useStyle = makeStyles((theme: Theme) => ({
  container: {
    '&, button': {
      lineHeight: 1.2,
    },
  },
  button: {
    '&:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
}));

const CustomToolbar: FC<CustomToolbarProps> = ({
  menuItems,
  onApplyFiltersClick,
  onApplySearchFiltersClick,
  onCustomButtonClick,
  isFiltered = false,
  showFilters,
  showSearchFilters = false,
  customButtonLabel,
  popoverFilters,
}) => {
  const classes = useStyle();
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [itemForConfirm, setItemForConfirm] = useState<CustomToolbarItem | null>(null);

  const clearItemForConfirm = () =>
    /* Leaving `confirmation` to make sure title and text won't dissaper before the animation */
    setItemForConfirm((prev) => ({ confirmation: prev?.confirmation } as CustomToolbarItem));

  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = ({ currentTarget }: any) => {
    setMenuAnchorEl(currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (item: CustomToolbarItem) => {
    handleMenuClose();

    if (item.confirmation) {
      return setItemForConfirm(item);
    }

    if (item && item.handler) {
      return item.data ? item.handler(item.data) : item.handler();
    }

    return null;
  };

  const onConfirm = () => {
    const { handler, data } = itemForConfirm!;

    if (data) {
      handler(data);
    } else {
      handler();
    }

    clearItemForConfirm();
  };

  const visibleMenuItems = menuItems
    ? menuItems.filter((item: CustomToolbarItem) => !item.hidden)
    : [];

  return (
    <Box display="flex" className={classes.container}>
      {popoverFilters}
      {onApplySearchFiltersClick && showSearchFilters && (
        <Button
          className={classes.button}
          size="small"
          variant={isFiltered ? 'contained' : 'outlined'}
          color={isFiltered ? 'primary' : undefined}
          onClick={onApplySearchFiltersClick}
        >
          {t('Refine your search')}
        </Button>
      )}
      {onApplyFiltersClick && showFilters && (
        <Button
          className={classes.button}
          size="small"
          variant={isFiltered ? 'contained' : 'outlined'}
          color={isFiltered ? 'primary' : undefined}
          onClick={onApplyFiltersClick}
        >
          {isFiltered ? t('Change filters') : t('Apply filters')}
        </Button>
      )}
      {onCustomButtonClick && customButtonLabel && (
        <Button
          className={classes.button}
          size="small"
          variant={isFiltered ? 'contained' : 'outlined'}
          color={isFiltered ? 'primary' : undefined}
          onClick={onCustomButtonClick}
        >
          {customButtonLabel}
        </Button>
      )}
      {visibleMenuItems.length > 0 && (
        <>
          <Button
            className={classes.button}
            size="small"
            variant="outlined"
            onClick={
              visibleMenuItems.length === 1
                ? () => handleMenuItemClick(visibleMenuItems[0])
                : handleMenuClick
            }
            endIcon={
              visibleMenuItems.length > 1 &&
              (menuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />)
            }
            disabled={visibleMenuItems.length === 1 ? visibleMenuItems[0].disabled : false}
          >
            {t(visibleMenuItems.length === 1 ? visibleMenuItems[0].text : 'More actions')}
          </Button>
          <Menu
            anchorEl={menuAnchorEl}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={menuOpen}
            onClose={handleMenuClose}
          >
            {visibleMenuItems.map((menuItem) => (
              <Tooltip title={menuItem.tooltip || ''} placement="top" key={menuItem.text}>
                <span>
                  <MenuItem
                    onClick={() => handleMenuItemClick(menuItem)}
                    disabled={menuItem.disabled}
                    style={{ color: menuItem.color }}
                  >
                    {t(menuItem.text)}
                  </MenuItem>
                </span>
              </Tooltip>
            ))}
          </Menu>
        </>
      )}
      <ConfirmModal
        open={!!itemForConfirm?.handler}
        onClose={clearItemForConfirm}
        onSuccess={onConfirm}
        title={itemForConfirm?.confirmation?.title!}
        text={itemForConfirm?.confirmation?.text!}
      />
    </Box>
  );
};

export default CustomToolbar;
